import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { fetchStory, formatDate } from "../../lib/newsApi";
import { safeUrl } from "../../lib/safeUrl";
import { PerspectiveChip } from "./NewsCard";
import { LocalActionCTA } from "./LocalActionCTA";
import { ShareMenu } from "../ShareMenu";

const Column = ({ title, items, tone }) => (
  <div className={`rounded-2xl border p-4 ${tone === "independent" ? "border-emerald-200 bg-emerald-50/40" : "border-sky-200 bg-sky-50/40"}`} data-testid={`story-column-${tone}`}>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">{title} · {items.length}</p>
    {items.length === 0 && <p className="mt-3 text-sm text-slate-500">Not covered by this side yet.</p>}
    <ul className="mt-3 space-y-3">
      {items.map((i) => (
        <li key={i.id} className="text-sm">
          <a href={safeUrl(i.link)} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:text-sky-900">{i.title}</a>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500"><span>{i.outlet}</span><PerspectiveChip value={i.perspective} /><span>{formatDate(i.published)}</span></div>
        </li>
      ))}
    </ul>
  </div>
);

const List = ({ label, items }) => items?.length ? (
  <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-slate-700">{items.map((x) => <li key={x}>{x}</li>)}</ul></div>
) : null;

export const Framing = ({ framing }) => framing ? (
  <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="story-framing">
    <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-violet-700"><Sparkles size={12} />AI-generated comparison · verify with the original articles</p>
    <p className="text-sm text-slate-800">{framing.summary}</p>
    <div className="grid gap-3 md:grid-cols-2">
      <div className="rounded-xl bg-sky-50 p-3 text-sm text-slate-700"><span className="font-semibold text-sky-900">Mainstream framing: </span>{framing.mainstream_framing}</div>
      <div className="rounded-xl bg-emerald-50 p-3 text-sm text-slate-700"><span className="font-semibold text-emerald-900">Independent framing: </span>{framing.independent_framing}</div>
    </div>
    {framing.key_themes?.length > 0 && <div className="flex flex-wrap gap-1.5">{framing.key_themes.map((t) => <span key={t} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">{t}</span>)}</div>}
    <div className="grid gap-4 md:grid-cols-2"><List label="Headline differences" items={framing.headline_differences} /><List label="Facts both sides report" items={framing.shared_facts} /></div>
  </div>
) : <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">A framing comparison appears once at least three articles from both sides cover this story.</p>;

export const StoryBody = ({ story }) => (
  <div className="space-y-4">
    <Framing framing={story.framing} />
    <div className="grid gap-4 md:grid-cols-2">
      <Column title="Mainstream" items={story.mainstream} tone="mainstream" />
      <Column title="Independent" items={story.independent} tone="independent" />
    </div>
  </div>
);

export const StoryDialog = ({ id, onClose }) => {
  const [story, setStory] = useState(null);
  useEffect(() => { setStory(null); if (id) fetchStory(id).then(setStory).catch(() => onClose()); }, [id, onClose]);
  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="story-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3 pr-6"><DialogTitle className="flex-1 font-serif text-2xl">{story?.label || "Loading story…"}</DialogTitle>{story && <ShareMenu path={`/news?story=${story.id}`} title={story.label} kind="story" testId="story-share" />}</div>
          <DialogDescription>{story ? `${story.size} items · ${story.source_ids.length} outlets · first seen ${formatDate(story.first_seen)}` : ""}</DialogDescription>
        </DialogHeader>
        {story && <StoryBody story={story} />}
        {story && <LocalActionCTA topics={story.topics} onNavigate={onClose} />}
      </DialogContent>
    </Dialog>
  );
};
